import React from 'react';
import {Box, Grid, Link, Stack, Typography, useTheme} from '@mui/material';
import {
  CardType,
  Custom_Encode_URL,
  DocumentCategories,
  IArtifact,
  PinTypes,
  ProcessTabs,
  ResourceTabs,
} from 'app/types/Shared';
import {ArtifactCards} from 'components/ArtifactCards';
import {SearchResultsExtension} from 'app/store/search/searchSlice';
import {SearchOff} from '@mui/icons-material';
import {ArtifactCard} from 'components/ArtifactCards/ArtifactCard';
import {useDispatch, useSelector} from 'react-redux';
import {selectAllProcesses} from 'app/store/operatingModel/operatingModelSlice';
import {EmptyState} from 'components/EmptyState/EmptyState';
import {
  setCurrentMapXL,
  setProcessPageTab,
  setProcessPageTabIndex,
  setSelectedProcess,
} from 'app/store/appState/appStateSlice';
import {useNavigate} from 'react-router-dom';
import {useFeatureFlag} from 'configcat-react';

interface SearchResultsProps {
  activeFilters: Array<DocumentCategories>;
  results: SearchResultsExtension;
  currentTab: ResourceTabs;
  storybook?: boolean;
  masterSOP?: IArtifact;
}

const SearchResultsView: React.FC<SearchResultsProps> = ({
  activeFilters,
  results,
  storybook,
  currentTab,
  masterSOP,
}) => {
  const {value: isChangeNotificationsEnabled} = useFeatureFlag('feature_change_notifications', false);
  const processDocuments = results.getProcessDocuments(activeFilters, currentTab);
  const processResults = results.getProcesses(currentTab);
  const processes = useSelector(selectAllProcesses);
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const resultsForFilter = () => {
    if (
      (activeFilters?.length === 0 || activeFilters.includes(DocumentCategories.Processes)) &&
      processDocuments.length
    ) {
      return true;
    } else {
      return false;
    }
  };

  const allProcesses = useSelector(selectAllProcesses);
  const getOpModel = (process_id: string) => {
    const model = allProcesses?.find((p) => p.process_id === process_id)?.operating_models_list;
    if (model) {
      return model;
    } else {
      return 'Hybrid';
    }
  };

  console.log('search results found: ', processDocuments.length);
  if (processDocuments.length === 0 && results.data.artifacts?.length === 0) {
    return (
      <Box
        width='100%'
        minHeight='55vh'
        display='flex'
        py={12}
        justifyContent='center'
        bgcolor={theme.palette.grey[100]}
      >
        <EmptyState
          icon={<SearchOff sx={{color: theme.palette.primary.main, mb: 0.5, width: 26, height: 26}} />}
          title='No results could be found for this search.'
          subtitle='Please try again with different keywords'
        />
      </Box>
    );
  }

  const renderOnboarding = (tab: ResourceTabs) => {
    const onboardingArtifacts =
      tab === ResourceTabs.FutureFit
        ? results.data.artifacts?.filter(
            (artifact) => artifact.type === 'Onboarding Video' || artifact.type === 'Onboarding Guide',
          )
        : [];

    if (onboardingArtifacts?.length) {
      return (
        <Box sx={{mb: 2, py: 2}}>
          <Typography variant='h4' sx={{mb: 2}}>
            Onboarding
          </Typography>
          <Grid container spacing={4}>
            {onboardingArtifacts?.map((item, index) => (
              <React.Fragment key={`${item.key}-${index}`}>
                <Grid item key={`${item.key}-${index}`} xs={12} sm={6} md={4} lg={3}>
                  <>
                    {storybook ? (
                      <ArtifactCard
                        data={{
                          ...item,
                          created_dt: item.created_dt.substring(0, 10), // truncate to date
                          card: CardType.document,
                        }}
                        pinned={true}
                      />
                    ) : (
                      <ArtifactCards
                        data={{
                          ...item,
                          created_dt: item.created_dt.substring(0, 10), // truncate to date
                          card: CardType.document,
                        }}
                      />
                    )}
                  </>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Box>
      );
    }
  };

  const navigateToProcess = (process_id?: string) => {
    const process = processes?.find((x) => x.process_id === process_id);
    if (process) {
      dispatch(setProcessPageTab(ProcessTabs.Map));
      dispatch(setProcessPageTabIndex(isChangeNotificationsEnabled ? 1 : 0));
      dispatch(setCurrentMapXL(false));
      dispatch(setSelectedProcess(process));
      navigate(`/process/${Custom_Encode_URL(process.name)}/${process.operating_models_list}`);
    }
  };

  const renderMasterSOP = () => {
    return (
      results.data.artifacts.some((artifact) => artifact.docid === masterSOP?.docid) &&
      (activeFilters?.length === 0 || activeFilters.includes(DocumentCategories.SOPs))
    );
  };

  return (
    <Stack spacing={2} sx={{px: 6, py: '16px', mx: '-24px'}} width='100%'>
      {masterSOP && renderMasterSOP() && (
        <Box sx={{borderBottom: `1px solid ${theme.palette.grey[500]}`, py: 2}}>
          <Grid container spacing={4}>
            <Grid item sm={12} md={6} lg={4} xl={3}>
              <>
                {storybook ? (
                  <ArtifactCard
                    data={{
                      ...masterSOP,
                      created_dt: masterSOP.created_dt?.substring(0, 10), // truncate to date
                      card: CardType.document,
                    }}
                    pinned={true}
                  />
                ) : (
                  <ArtifactCards
                    data={{
                      ...masterSOP,
                      created_dt: masterSOP.created_dt?.substring(0, 10), // truncate to date
                      card: CardType.document,
                    }}
                  />
                )}
              </>
            </Grid>
          </Grid>
        </Box>
      )}
      {processDocuments.map((item, index) => (
        <React.Fragment key={`${item.process.process_id}-${index}`}>
          {resultsForFilter() && (
            <Box sx={{borderBottom: `1px solid ${theme.palette.grey[500]}`, py: 2}}>
              <Box sx={{mb: 2}}>
                <Link
                  underline='hover'
                  sx={{color: 'black', cursor: 'pointer', ':&hover': {color: theme.palette.primary.main}}}
                  onClick={() => navigateToProcess(item.process.process_id)}
                >
                  <Typography variant='h5' sx={{mb: 2}}>
                    {item.process.name}
                  </Typography>
                </Link>
                <Grid container spacing={4}>
                  <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                    <>
                      {processResults.some((process) => process.process_id === item.process.process_id) && (
                        <Box>
                          {storybook ? (
                            <ArtifactCard
                              data={{
                                id: item.process.id,
                                title: item.process.name,
                                created_dt: item.process.last_modified_dt,
                                key: `LINK-${item.process.name.replace(' ', '-')}`,
                                url: `${window.location.origin}/process/${item.process.name.replace(
                                  ' ',
                                  '-',
                                )}/${getOpModel(item.process.process_id)}`,
                                type: PinTypes.Process,
                                card: CardType.process,
                              }}
                              pinned={false}
                            />
                          ) : (
                            <ArtifactCards
                              data={{
                                id: item.process.id,
                                title: item.process.name,
                                created_dt: item.process.last_modified_dt,
                                key: item.process.process_id,
                                url: `${window.location.origin}/process/${Custom_Encode_URL(
                                  item.process.name,
                                )}/${getOpModel(item.process.process_id)}`,
                                type: PinTypes.Process,
                                card: CardType.process,
                              }}
                            />
                          )}
                        </Box>
                      )}
                    </>
                  </Grid>
                </Grid>
              </Box>
              {item.artifacts.length > 0 && (
                <Box sx={{mb: 2}}>
                  <Grid container spacing={4}>
                    {item.artifacts.map((artifact, index) => (
                      <Grid item key={index} sm={12} md={6} lg={4} xl={3}>
                        <>
                          {storybook ? (
                            <ArtifactCard
                              data={{
                                ...artifact,
                                created_dt: artifact.created_dt?.substring(0, 10), // truncate to date
                                card: CardType.document,
                              }}
                              pinned={true}
                            />
                          ) : (
                            <ArtifactCards
                              data={{
                                ...artifact,
                                created_dt: artifact.created_dt?.substring(0, 10), // truncate to date
                                card: CardType.document,
                              }}
                            />
                          )}
                        </>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
          )}
          {item.artifacts.length > 0 && !resultsForFilter() && (
            <Box sx={{borderBottom: `1px solid ${theme.palette.grey[500]}`, py: 2}}>
              <Link
                underline='hover'
                sx={{color: 'black', cursor: 'pointer', ':&hover': {color: theme.palette.primary.main}}}
                onClick={() => navigateToProcess(item.process.process_id)}
              >
                <Typography variant='h5' sx={{mb: 2}}>
                  {item.process.name}
                </Typography>
              </Link>
              <Grid container spacing={4}>
                {item.artifacts.map((artifact, index) => (
                  <Grid item key={index} sm={12} md={6} lg={4} xl={3}>
                    <>
                      {storybook ? (
                        <ArtifactCard
                          data={{
                            ...artifact,
                            created_dt: artifact.created_dt?.substring(0, 10), // truncate to date
                            card: CardType.document,
                          }}
                          pinned={true}
                        />
                      ) : (
                        <ArtifactCards
                          data={{
                            ...artifact,
                            created_dt: artifact.created_dt?.substring(0, 10), // truncate to date
                            card: CardType.document,
                          }}
                        />
                      )}
                    </>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </React.Fragment>
      ))}
      {/*Display Onboarding artifacts, filtered by tab*/}
      {renderOnboarding(currentTab)}
    </Stack>
  );
};

export default SearchResultsView;
